import React, { ElementType, FC, ReactNode, useEffect, useMemo } from 'react';

import {
  CircularProgress,
  FormControl,
  FormHelperText,
  InputAdornment,
  MenuItem,
  MenuItemProps,
  Select as MuiSelect,
  SelectChangeEvent,
  SelectProps as MuiSelectProps,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import map from 'lodash/map';

import { Density } from '@sbv-clients/util-misc';

import { withTitle } from '../../hoc/with-title';
import {
  useWindowedTooltipZIndex,
  withWindowedMenuProps,
} from '../dialog/windowed-tooltip-zindex-context';
import { Icon, IconName } from '../icon';
import { useScaledMenuProps } from './use-scaled-menu-props';

export type SelectOption<T> = {
  id: string | number;
  label?: ReactNode;
  value?: number | string;
  disabled?: boolean;
  inActive?: boolean;
  meta?: any;
};

type SelectProps<T> = Omit<
  MuiSelectProps<T>,
  'onChange' | 'value' | 'renderValue'
> & {
  firstOptionAsDefault?: boolean;
  isLoading?: boolean;
  options?: SelectOption<T>[];
  value?: T;
  helperText?: ReactNode;
  MenuItem?: FC<MenuItemProps>;
  sx?: MuiSelectProps<T>['sx'];
  iconName?: IconName;
  onChange?: (event: SelectChangeEvent<T>, child: ReactNode, meta: any) => void;
  hideStartAdornment?: boolean;
  notSelectedLabel?: string;
  applyDataGridInputStyles?: boolean;
  defaultOpen?: boolean;
  renderValue?: (selected: unknown) => any;
  placeholder?: string;
  density?: Density;
};

const dataGridInputStyles = (applyDataGridInputStyles?: boolean) => {
  if (!applyDataGridInputStyles) {
    return {};
  }
  return {
    borderRadius: 0,
    '& .MuiOutlinedInput-notchedOutline': {
      borderRadius: 0,
      fontSize: '13px',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderRadius: 'none',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderRadius: 'none',
    },
  } as any;
};

const SelectNoTitle = <T,>({
  firstOptionAsDefault,
  isLoading,
  required,
  value,
  size,
  name,
  onChange,
  onBlur,
  label,
  helperText,
  error,
  fullWidth,
  disabled,
  options = [],
  className,
  MenuProps,
  onClose,
  onOpen,
  onClick,
  open,
  sx,
  iconName = 'KeyboardArrowDown',
  hideStartAdornment,
  notSelectedLabel = 'Not Selected',
  applyDataGridInputStyles,
  defaultOpen,
  renderValue,
  density = 'compact',
}: SelectProps<T>) => {
  const windowedConfig = useWindowedTooltipZIndex();
  const {
    anchorRef,
    measure,
    menuProps: scaledMenuProps,
  } = useScaledMenuProps(MenuProps, open);
  const theme = useTheme();
  const selected = value !== '' && value !== null && value !== undefined;

  const startAdornment = useMemo(() => {
    if (hideStartAdornment) return null;
    if (isLoading) {
      return (
        <InputAdornment position="start">
          <CircularProgress size={14} />
        </InputAdornment>
      );
    }
    if (selected) return null;
    return (
      <InputAdornment
        position="start"
        sx={{ width: '0px !important', color: theme.text.placeholder }}
      >
        <span style={{ fontSize: 'small' }}>{notSelectedLabel}</span>
      </InputAdornment>
    );
  }, [isLoading, selected, hideStartAdornment, notSelectedLabel, theme]);
  const SelectIcon: ElementType = (iconProps: any) => (
    <Icon
      name={iconName}
      sx={{
        alignItems: 'end',
        justifyContent: 'end',
        display: 'flex',
        width: 'auto !important',
      }}
      {...iconProps}
    />
  );
  useEffect(() => {
    if (!firstOptionAsDefault || selected || !options.length) {
      return;
    }

    const firstOption = options[0];

    if (!firstOption?.value) return;

    const fakeEvent = {
      target: { value: firstOption.value },
    } as SelectChangeEvent<T>;

    onChange?.(fakeEvent, firstOption.label, firstOption.meta);
  }, [firstOptionAsDefault, selected, options, onChange]);
  return (
    <FormControl
      size={size}
      fullWidth={fullWidth}
      required={required}
      error={error}
      disabled={disabled || isLoading}
      className={className}
    >
      <MuiSelect
        defaultOpen={defaultOpen}
        disabled={disabled || isLoading}
        onClick={onClick}
        ref={anchorRef}
        onOpen={(event) => {
          measure();
          onOpen?.(event);
        }}
        name={name}
        error={error}
        open={open}
        value={!selected ? '' : value ?? ('' as any)}
        onChange={(event, child) => {
          const meta = options?.find(
            (item) => item.value === event.target.value
          )?.meta;
          onChange?.(event as SelectChangeEvent<T>, child, meta);
        }}
        onBlur={onBlur}
        MenuProps={withWindowedMenuProps(windowedConfig, scaledMenuProps)}
        onClose={onClose}
        sx={() => ({
          ...sx,
          ...dataGridInputStyles(applyDataGridInputStyles),
          height: density === 'comfortable' ? '42px' : '32px',
        })}
        startAdornment={startAdornment}
        IconComponent={SelectIcon}
        renderValue={renderValue}
      >
        {map(options, (item) => {
          const inactiveStyles = item.inActive ? { opacity: 0.5 } : {};
          return (
            <MenuItem
              key={item.id}
              value={item.value}
              disabled={item.disabled}
              style={{ fontSize: 'smaller', ...inactiveStyles }}
            >
              {item.label}
            </MenuItem>
          );
        })}
      </MuiSelect>
      {error && <FormHelperText error>{helperText}</FormHelperText>}
      {!error && disabled && helperText && (
        <FormHelperText>{helperText}</FormHelperText>
      )}
    </FormControl>
  );
};

const Select = withTitle(SelectNoTitle);
export type { SelectProps, SelectChangeEvent };
export { Select, MuiSelect };
export default withTitle(Select);
